// sbu-letter-filter.js — the A–Z bar on sections/sbu-name-index.liquid (added 2026-09-16,
// operator: "add an Alphabet A,B,.. so that someone can click that and see the subset").
//
// Progressive enhancement. Without this file the letters are #letter-L anchors and every
// group renders. With it, a letter hides the other groups, "All" shows them all,
// aria-current moves to the pressed tile, the polite live status names the letter and
// recounts ("Letter M: 3 authors · 4 books"), and the URL carries ?letter=L via
// replaceState (no hash, so no scroll jump).
// On load ?letter= is restored and a #letter-L hash (the tile's own href, what "Copy link"
// copies) filters too, as do a later hashchange and Back/Forward (2026-09-16, review
// minor 2). An unknown value (a stale link) falls back to All rather than rendering an
// empty page, and the URL is tidied to ?letter= of whatever was applied. Keys are "A"–"Z" and "other";
// a lower-case single letter is accepted.
//
// Books in the status come from each group's data-books (distinct products, counted in
// Liquid), not from summing card counts, so a co-authored book is one book.
// Keyboard: the tiles are links (the no-JS contract), but they act as filter buttons, so
// Space activates a focused tile as Enter does, instead of scrolling the page (2026-09-16,
// review minor 1). Nothing is modal, so Escape is not intercepted.
class SbuLetterFilter extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener(
      'click',
      (event) => {
        const tile = event.target.closest('a.sbu-name-index__letter');
        if (!tile || !this.contains(tile)) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.#select(tile.dataset.letter ?? '');
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== ' ' || event.repeat) return;
        const tile = event.target.closest?.('a.sbu-name-index__letter');
        if (!tile || !this.contains(tile)) return;
        event.preventDefault();
        this.#select(tile.dataset.letter ?? '');
      },
      { signal }
    );

    // A hash navigation (#letter-L) and Back/Forward both re-read the URL. #sync rewrites the
    // entry it lands on with replaceState, so a hash navigation's new entry becomes ?letter=L
    // and Back returns to the previous entry, whose popstate restores that entry's letter
    // (2026-09-16, review: without popstate, Back changed the URL but not the filter).
    window.addEventListener('hashchange', () => this.#sync(false), { signal });
    window.addEventListener('popstate', () => this.#sync(true), { signal });

    this.#sync(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // The letter a #letter-L hash names, or null when the hash is not a letter hash.
  // A malformed escape (#letter-%E0) makes decodeURIComponent throw; the raw text is
  // returned instead, which names no group, so #apply falls back to All and #sync
  // drops the bad hash from the URL (2026-09-16, review: the throw left the
  // filter on the old letter and the hash in the URL).
  #hashLetter() {
    const m = /^#letter-(.+)$/.exec(location.hash);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }

  // Applies a letter and records it in ?letter=, dropping any hash.
  #select(raw) {
    const applied = this.#apply(raw);
    const url = new URL(location.href);
    if (applied) url.searchParams.set('letter', applied);
    else url.searchParams.delete('letter');
    url.hash = '';
    history.replaceState(history.state, '', url);
  }

  // Filters from the current URL, then writes back what was applied. A #letter-L hash wins
  // over ?letter= (it is the newer intent: a tile link followed from a filtered page) and is
  // folded into ?letter=; a stale, unknown or re-cased value is corrected or removed, so the
  // URL never names a filter the page is not showing. This covers first load, hashchange and
  // popstate alike (2026-09-16, review: a #letter-L on first load was applied but left in the
  // URL as typed, and an unknown one stayed while All was shown). A non-letter hash is left
  // alone. With nothing in the URL: on load nothing is touched; on popstate All is restored.
  #sync(always) {
    const fromHash = this.#hashLetter();
    const fromQuery = new URLSearchParams(location.search).get('letter');
    const wanted = fromHash ?? fromQuery ?? '';
    if (!wanted && !always && fromQuery === null) return;

    const applied = this.#apply(wanted);
    const url = new URL(location.href);
    if (applied) url.searchParams.set('letter', applied);
    else url.searchParams.delete('letter');
    if (fromHash !== null) url.hash = '';
    if (url.href !== location.href) history.replaceState(history.state, '', url);
  }

  // Shows only the group keyed `letter` ('' = all). Returns the key actually applied.
  #apply(raw) {
    let letter = raw.length === 1 ? raw.toUpperCase() : raw.toLowerCase();
    const groups = [...this.querySelectorAll('.sbu-name-index__group')];
    if (letter && !groups.some((g) => g.dataset.letter === letter)) letter = '';

    let names = 0;
    let books = 0;
    for (const group of groups) {
      const show = !letter || group.dataset.letter === letter;
      group.hidden = !show;
      if (show && letter) {
        names += group.querySelectorAll('.sbu-name-index__item').length;
        books += Number(group.dataset.books) || 0;
      }
    }

    let label = '';
    for (const tile of this.querySelectorAll('a.sbu-name-index__letter')) {
      if ((tile.dataset.letter ?? '') === letter) {
        tile.setAttribute('aria-current', 'true');
        label = tile.textContent.trim();
      } else tile.removeAttribute('aria-current');
    }

    const status = this.querySelector('.sbu-name-index__status');
    if (status) {
      const noun = status.dataset.noun || 'authors';
      if (!letter) {
        names = Number(status.dataset.totalNames) || 0;
        books = Number(status.dataset.totalBooks) || 0;
      }
      const counts = `${names} ${names === 1 ? noun.replace(/s$/, '') : noun} · ${books} ${books === 1 ? 'book' : 'books'}`;
      // The letter is named so five one-author letters do not all announce "1 author ·
      // 1 book" (2026-09-16, review minor 3). "Other" is a label, not a letter.
      status.textContent = !letter ? counts : letter === 'other' ? `${label || 'Other'}: ${counts}` : `Letter ${letter}: ${counts}`;
    }
    return letter;
  }
}

if (!customElements.get('sbu-letter-filter')) customElements.define('sbu-letter-filter', SbuLetterFilter);
